// mj-story-hero: loaded only where the section is used (sections/mj-story-hero.liquid)
// Slides advance when their progress bar completes (a CSS animation, so pausing freezes it exactly).
// Phones: tap right for next, left third for previous, press and hold to pause, horizontal swipe also works.
// Vertical scrolling is never blocked. Pauses off screen and in background tabs; static on reduced motion.

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const HOLD_MS = 220;

class MjStoryHero extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-mj-slide]')];
    this.bars = [...this.querySelectorAll('[data-mj-bar]')];
    this.toggle = this.querySelector('[data-mj-story-toggle]');
    this.stage = this.querySelector('[data-mj-stage]');
    this.index = 0;
    this.userPaused = false;
    this.holding = false;
    this.inView = true;
    this.style.setProperty('--mj-story-duration', `${Number(this.dataset.duration) || 6}s`);
    if (this.slides.length < 2) return;

    this.static = reducedMotion();
    this.classList.toggle('is-static', this.static);

    this.addEventListener('click', this.onClick);
    this.bars.forEach((bar) => bar.addEventListener('animationend', this.onBarEnd));
    this.stage.addEventListener('pointerdown', this.onPointerDown);

    this.observer = new IntersectionObserver((entries) => {
      this.inView = entries.some((entry) => entry.isIntersecting);
      this.syncPause();
    }, { threshold: 0.25 });
    this.observer.observe(this);
    document.addEventListener('visibilitychange', this.syncPause);

    this.onEditorSelect = (event) => {
      const slide = event.target.closest?.('[data-mj-slide]');
      if (slide && this.contains(slide)) {
        this.go(this.slides.indexOf(slide));
        this.editorHold = true;
        this.syncPause();
      }
    };
    this.onEditorDeselect = () => {
      this.editorHold = false;
      this.syncPause();
    };
    document.addEventListener('shopify:block:select', this.onEditorSelect);
    document.addEventListener('shopify:block:deselect', this.onEditorDeselect);

    this.go(0);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.syncPause);
    document.removeEventListener('shopify:block:select', this.onEditorSelect);
    document.removeEventListener('shopify:block:deselect', this.onEditorDeselect);
  }

  go(index) {
    const count = this.slides.length;
    this.index = (index + count) % count;

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.querySelectorAll('mj-video').forEach((video) => video.toggleAttribute('inactive', !active));
    });

    this.bars.forEach((bar, i) => {
      bar.classList.remove('is-active');
      bar.classList.toggle('is-done', i < this.index);
    });
    const bar = this.bars[this.index];
    if (bar) {
      void bar.offsetWidth; // restart the fill animation
      bar.classList.add('is-active');
    }
    this.syncPause();
  }

  next = () => this.go(this.index + 1);
  prev = () => this.go(this.index - 1);

  onBarEnd = (event) => {
    if (event.animationName === 'mj-story-fill' && event.currentTarget === this.bars[this.index]) this.next();
  };

  onClick = (event) => {
    if (event.target.closest('[data-mj-prev]')) return this.prev();
    if (event.target.closest('[data-mj-next]')) return this.next();
    if (event.target.closest('[data-mj-story-toggle]')) {
      this.userPaused = !this.userPaused;
      this.syncPause();
    }
  };

  get paused() {
    return this.static || this.userPaused || this.holding || this.editorHold || !this.inView || document.hidden;
  }

  syncPause = () => {
    const paused = this.paused;
    this.classList.toggle('is-paused', paused);
    this.stage?.setAttribute('aria-live', paused ? 'polite' : 'off');
    this.querySelectorAll('mj-video').forEach((video) => video.setPaused?.(this.userPaused || this.holding));
    if (this.toggle) {
      const showPlay = this.userPaused || this.static;
      this.toggle.setAttribute('aria-pressed', String(showPlay));
      this.toggle.setAttribute('aria-label', showPlay ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
      this.toggle.hidden = this.static;
    }
  };

  // Touch: tap left/right, press and hold to pause, horizontal swipe
  onPointerDown = (event) => {
    if (event.pointerType === 'mouse' || event.target.closest('a, button')) return;
    try {
      this.stage.setPointerCapture(event.pointerId);
    } catch (error) {}
    const startX = event.clientX;
    const startY = event.clientY;
    const startTime = performance.now();
    let cancelled = false;
    const holdTimer = setTimeout(() => {
      this.holding = true;
      this.syncPause();
    }, HOLD_MS);

    const finish = (endEvent) => {
      clearTimeout(holdTimer);
      this.stage.removeEventListener('pointerup', finish);
      this.stage.removeEventListener('pointercancel', cancel);
      const wasHolding = this.holding;
      this.holding = false;
      if (cancelled) return this.syncPause();

      const dx = endEvent.clientX - startX;
      const dy = endEvent.clientY - startY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        dx < 0 ? this.next() : this.prev();
      } else if (!wasHolding && performance.now() - startTime < HOLD_MS && Math.abs(dy) < 12) {
        const rect = this.stage.getBoundingClientRect();
        endEvent.clientX - rect.left < rect.width / 3 ? this.prev() : this.next();
      } else {
        this.syncPause();
      }
    };
    // The browser takes over for vertical scrolling: no tap, just resume
    const cancel = () => {
      cancelled = true;
      finish(event);
    };
    this.stage.addEventListener('pointerup', finish);
    this.stage.addEventListener('pointercancel', cancel);
  };
}

if (!customElements.get('mj-story-hero')) customElements.define('mj-story-hero', MjStoryHero);
